import { TriangleAlert } from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  formatDateRange,
  formatHours,
  formatOptionalHours,
  hoursValue,
  MONTH_ABBREVIATIONS,
} from "@/lib/persons/project-format";
import {
  buildGuidanceGrid,
  currentMonthKey,
  formatRoleLabel,
  type GuidanceGridCell,
  type GuidanceMonth,
  type PersonProjectAssignment,
  stripAccountPrefix,
  sumGuidedHours,
} from "@/lib/persons/project-guidance";
import { cn } from "@/lib/utils";

/** NOS assignment status → badge tone. Unknown statuses fall back to neutral. */
const ASSIGNMENT_TONES: Record<string, StatusTone> = {
  Active: "success",
  Committed: "info",
  Onboarding: "info",
  Proposed: "neutral",
  "Rolled Off": "neutral",
};

/**
 * Everything about one assignment that the card deliberately leaves out: the
 * SOW it belongs to, its status and dates, the SOW-lifetime totals, and the
 * month-by-month breakdown.
 */
export function ProjectDetailDialog({
  assignment,
  open,
  onOpenChange,
}: {
  assignment: PersonProjectAssignment | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  // The dialog animates out over 200ms, so it has to keep rendering the
  // assignment it was opened with after the caller clears it — dropping the
  // content on close flashes an empty box on the way out.
  const last = useRef<PersonProjectAssignment | null>(null);
  if (assignment) last.current = assignment;
  const shown = assignment ?? last.current;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Wide enough that the content isn't scrolled to read: the month strip
          is the widest thing here and gets ~16 months in view at this size,
          which covers most SOWs outright. `overflow-y-auto` stays as the
          safety valve for the outliers (a five-year SOW, an expanded 40-project
          list) rather than as the normal way this reads. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        {shown && <ProjectDetail assignment={shown} />}
      </DialogContent>
    </Dialog>
  );
}

function ProjectDetail({ assignment }: { assignment: PersonProjectAssignment }) {
  const {
    demandName,
    accountName,
    projectNames,
    hoursLogged,
    assignmentStatus,
    demandStatus,
    role,
    roleLevel,
    isResponsible,
    isAccountable,
    startDate,
    endDate,
    months,
    guidance,
  } = assignment;

  const roleLabel = formatRoleLabel(role, roleLevel);
  const range = formatDateRange(startDate, endDate);
  const statusLabel = assignmentStatus ?? demandStatus;
  const tone: StatusTone = assignmentStatus
    ? (ASSIGNMENT_TONES[assignmentStatus] ?? "neutral")
    : "neutral";
  const sowName = stripAccountPrefix(demandName, accountName);
  const totalGuided = sumGuidedHours(months);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{accountName ?? demandName}</DialogTitle>
        {/* The SOW name, with the account stripped off the front — NOS demand
            names embed it, so printing both raw shows the account twice. */}
        <DialogDescription>{sowName}</DialogDescription>
      </DialogHeader>

      {/* `min-w-0`: this is a grid item of DialogContent, and a grid item
          defaults to `min-width: auto` — so without it a long SOW's month strip
          refuses to shrink, blows the box out to its full 3000px+ and scrolls
          the ENTIRE dialog sideways instead of scrolling inside the strip. */}
      <div className="min-w-0 space-y-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {isAccountable && <StatusPill tone="navy" label="Accountable" />}
          {isResponsible && <StatusPill tone="navy" label="Responsible" />}
          {statusLabel && <StatusPill tone={tone} label={statusLabel} />}
          {roleLabel && <span className="text-sm text-muted-foreground">{roleLabel}</span>}
        </div>

        {range && <p className="text-sm text-muted-foreground">{range}</p>}

        {/* Totals span the whole SOW, not the banner's rolling window — "total"
            has to mean the contract's total or the figure moves each month. */}
        <div className="grid grid-cols-2 gap-3">
          <Total
            label="Total guidance"
            value={formatOptionalHours(totalGuided)}
            hint="Guided across every month of this SOW"
          />
          <Total
            label="Total hours worked"
            value={formatHours(hoursLogged)}
            hint="Logged against this assignment to date"
          />
        </div>

        {projectNames.length > 0 && <ProjectNames names={projectNames} />}

        <div>
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              By month
              {months.length > 0 && (
                <span className="ml-2 font-normal normal-case tracking-normal">
                  {months.length} {months.length === 1 ? "month" : "months"} on this SOW
                </span>
              )}
            </h4>
            {/* The grid has a row per year, so "Guided"/"Logged" can no longer
                label the rows from the left — they become a key stated once. */}
            {months.length > 0 && (
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Guided</span> over logged, in hours
              </p>
            )}
          </div>

          {/* The strip renders on any month data, but "no guidance set" is
              still called out above it — an assignment can have logged time and
              no DemandPlan row at all, and that unplanned case is the whole
              point of the flag. Showing the strip alone would bury it. */}
          {!guidance && (
            <p className="mb-2 inline-flex items-center gap-1.5 text-sm text-warning-foreground">
              <TriangleAlert className="size-3.5" />
              No guidance set.
            </p>
          )}

          {months.length > 0 && <SowMonthGrid months={months} />}
        </div>

        {guidance?.note && <p className="text-sm">{guidance.note}</p>}
      </div>
    </>
  );
}

function Total({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-lg bg-secondary px-3 py-2">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xl font-semibold leading-tight tabular-nums">{value}</div>
      <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/** How many project names the popup lists before collapsing the rest. */
const PROJECT_NAMES_SHOWN = 6;

/**
 * The projects drawn from logged time. An account-management assignment can
 * accumulate 40+ of these, so the list is capped and the rest put behind a
 * toggle.
 */
function ProjectNames({ names }: { names: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const hidden = names.length - PROJECT_NAMES_SHOWN;
  const visible = expanded ? names : names.slice(0, PROJECT_NAMES_SHOWN);

  return (
    <p className="text-sm text-muted-foreground">
      Projects: <span className="text-foreground">{visible.join(", ")}</span>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 font-medium text-foreground underline underline-offset-2 hover:no-underline"
        >
          {expanded ? "show less" : `+${hidden} more`}
        </button>
      )}
    </p>
  );
}

/**
 * Every month the SOW spans, as a calendar: twelve columns, one row per year.
 *
 * A SOW can run five years, which as a single line of months is 3000px of
 * horizontal scrolling. As a calendar it is five rows that always fit the
 * popup's width — and each month sits directly above the same month a year
 * later, so year-over-year reads at a glance.
 */
function SowMonthGrid({ months }: { months: GuidanceMonth[] }) {
  const years = buildGuidanceGrid(months);
  const current = currentMonthKey();

  return (
    // A fixed twelve columns plus a year gutter. `minmax(0,1fr)` rather than
    // `1fr` so a wide cell can't push the grid past the popup — `1fr` floors at
    // min-content, which is the same trap the scrolling strip fell into.
    <div className="grid grid-cols-[auto_repeat(12,minmax(0,1fr))] gap-x-1 gap-y-1">
      <div />
      {MONTH_ABBREVIATIONS.map((label) => (
        <div
          key={label}
          className="pb-0.5 text-center text-[0.6875rem] font-medium leading-4 text-muted-foreground"
        >
          {label}
        </div>
      ))}

      {years.map((row) => (
        <Fragment key={row.year}>
          <div className="self-center pr-1 text-right text-xs font-medium tabular-nums text-muted-foreground">
            {row.year}
          </div>
          {row.cells.map((cell) => (
            <MonthCell key={cell.month} cell={cell} isCurrent={cell.month === current} />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

function MonthCell({ cell, isCurrent }: { cell: GuidanceGridCell; isCurrent: boolean }) {
  // Outside the SOW entirely: no border, no dash, nothing. A SOW that started
  // in September did not have eight months of zero — it did not exist yet, and
  // a row of dashes would claim otherwise.
  if (!cell.inSpan) return <div />;

  const isPast = cell.month < currentMonthKey();
  const { entry } = cell;

  return (
    <div
      className={cn(
        "rounded-md border px-1 py-1 text-center",
        isCurrent && "border-primary/50 bg-primary/10",
        !isCurrent && isPast && "border-transparent bg-secondary opacity-60",
        !isCurrent && !isPast && "border-transparent bg-secondary",
      )}
    >
      {/* Guided over logged, per the key above the grid. */}
      <div className="text-sm font-semibold leading-5 tabular-nums">
        {entry?.hours !== null && entry?.hours !== undefined ? (
          hoursValue(entry.hours)
        ) : (
          <span className="font-normal text-muted-foreground">—</span>
        )}
      </div>
      <div
        className={cn(
          "text-sm leading-5 tabular-nums",
          entry?.loggedHours !== null && entry?.loggedHours !== undefined
            ? "text-foreground"
            : "text-muted-foreground",
        )}
      >
        {entry?.loggedHours !== null && entry?.loggedHours !== undefined
          ? hoursValue(entry.loggedHours)
          : "—"}
      </div>
    </div>
  );
}
